/* Direct Jantes V36.68 — original layout, compact vehicle and combined delivery total. */
import { readVehicleContext } from './directjantes-vehicle-context.js';
import { createConfiguration, scrollToConfiguration } from '@theme/directjantes-vehicle-configuration';

const EXPERT = 'Montage conseillé par nos experts';
const MANUAL = 'Montage personnalisé';
const LABELS = { expert: 'Montage conseillé', manual: 'Choisir mes dimensions' };

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function context(url) {
  let car = readVehicleContext(url);
  if (!car) {
    try {
      const saved = sessionStorage.getItem('dj-vehicle-listing-v1');
      if (saved) car = readVehicleContext(new URL(saved, location.origin));
    } catch { /* Links carry the context even when storage is unavailable. */ }
  }
  return car;
}

function configurationPanel(root, suffix) {
  const grid = root.querySelector('.dj-product-data-grid');
  const specs = grid?.querySelector('.dj-product-spec-card');
  const dimensions = grid?.querySelector('.dj-product-dimensions');
  if (!grid || !dimensions) return null;
  const details = element('details', 'dj-vp-accordion dj-vc-block');
  const summary = element('summary', 'dj-vp-summary');
  summary.id = `dj-vc-heading-${suffix}`;
  const heading = element('h2', '', 'Fiche technique et dimensions');
  const icon = dimensions.querySelector('h2 svg')?.cloneNode(true);
  if (icon) heading.prepend(icon);
  const chevron = element('span', 'dj-vp-chevron');
  chevron.setAttribute('aria-hidden', 'true');
  summary.append(heading, chevron);
  const panel = element('div', 'dj-vc-columns');
  panel.id = `dj-vc-panel-${suffix}`;
  panel.setAttribute('aria-labelledby', summary.id);
  if (specs) panel.append(specs);
  panel.append(dimensions);
  const guide = element('div', 'dj-vc-guide');
  const info = element('span', 'dj-vc-guide-icon', 'i');
  info.setAttribute('aria-hidden', 'true');
  const explanation = element('div', 'dj-vc-guide-content');
  for (const [label, text] of [
    [EXPERT, 'nous choisissons les dimensions adaptées à votre véhicule, dans le diamètre sélectionné.'],
    [MANUAL, 'choisissez vous-même les dimensions et le montage parmi les options ci-dessous.'],
  ]) {
    const paragraph = element('p');
    paragraph.append(element('strong', '', `${label} : `), document.createTextNode(text));
    explanation.append(paragraph);
  }
  guide.append(info, explanation);
  details.append(summary, guide, panel);
  grid.replaceChildren(details);
  grid.classList.add('dj-vc-unified');
  return { details, panel, summary, specs, dimensions };
}

function orderPurchaseBlocks(root, form) {
  const purchase = form.closest('.buy-buttons-block');
  const parent = purchase?.parentElement;
  if (!parent) return;
  // Move the complete Shopify blocks, preserving reading/tab order and keeping
  // the native purchase form connected with its existing event handlers.
  for (const selector of ['.dj-product-assistance', '.dj-product-shipping']) {
    let block = root.querySelector(selector);
    if (!block || !parent.contains(block)) continue;
    while (block.parentElement !== parent) block = block.parentElement;
    if (block !== purchase) parent.insertBefore(block, purchase);
  }
  parent.classList.add('dj-vp-purchase-content');
}

function refinePresentation(root, form, canPair) {
  // Only an existing automobile brand in the product title is relabelled.
  // Wheel brands (Haxer, Racingline…) and supplier data remain untouched.
  const title = root.querySelector('.dj-product-heading__title');
  for (const text of title?.childNodes || []) {
    if (text.nodeType !== Node.TEXT_NODE) continue;
    text.textContent = text.textContent.replace(/^(\s*Jantes?)\s+(?=(?:Audi|BMW|Mercedes(?:[- ]Benz)?|Volkswagen|VW|Porsche|Peugeot|Renault|Citroën|Citroen|Ford|Tesla|Toyota|Skoda|Škoda|Seat|Cupra|Volvo|Opel|Mini|Fiat|Alfa Romeo|Land Rover|Range Rover|Jaguar|Nissan|Honda|Hyundai|Kia|Mazda|Suzuki|Dacia|BYD)\b)/i, '$1 compatible ');
    break;
  }
  const stock = root.querySelector('.dj-product-availability strong');
  if (stock) stock.textContent = stock.textContent.replace(/pièces?/i, match => match.endsWith('s') ? 'jantes' : 'jante');
  const media = root.querySelector('.product-information__media');
  if (media) {
    const caption = element('p', 'dj-vp-photo-caption', 'Jantes vendues sans pneus');
    const certifications = media.querySelector('.dj-vp-certifications');
    if (certifications) {
      certifications.before(caption);
      certifications.hidden = false;
    } else media.append(caption);
    const gallery = media.querySelector('media-gallery');
    const hint = media.querySelector('.dj-product-photo-hint');
    const photoFrames = [...(gallery?.querySelectorAll('.product-media-container--image') || [])]
      .filter(frame => !frame.closest('.dialog-zoomed-gallery'));
    if (hint && photoFrames.length) {
      for (const frame of photoFrames) frame.append(hint.cloneNode(true));
      hint.remove();
    }
    media.classList.add('dj-vp-captioned-media');
  }
  const rule = root.querySelector('.dj-product-trust-strip > div:nth-child(2) span');
  if (rule) rule.textContent = canPair ? 'Lots de 4 — identiques ou 2+2 selon le montage' : 'Lots de 4 jantes identiques';
  const input = form.querySelector('[name="quantity"]');
  const wrapper = input?.closest('.quantity-selector-wrapper');
  if (input && wrapper) {
    if (!input.id) input.id = `dj-vp-quantity-${form.id || 'product'}`;
    const label = element('label', 'dj-vp-quantity-label', 'Quantité de jantes');
    label.htmlFor = input.id;
    input.setAttribute('aria-label', 'Quantité de jantes');
    wrapper.prepend(label);
    form.querySelector('.quantity-label')?.setAttribute('hidden', '');
  }
}

function deliveredTotal(root, form, hasConfiguration) {
  const purchase = form.closest('.buy-buttons-block');
  if (!purchase) return;
  const box = element('div', 'dj-vp-total');
  box.setAttribute('role', 'status');
  box.setAttribute('aria-live', 'polite');
  box.setAttribute('aria-atomic', 'true');
  const copy = element('div', 'dj-vp-total-copy');
  const label = element('strong', 'dj-vp-total-label');
  const note = element('span', 'dj-vp-total-note');
  const amount = element('strong', 'dj-vp-total-amount');
  copy.append(label, note); box.append(copy, amount);
  const deliveryBlock = root.querySelector('directjantes-product-shipping');
  // Keep the existing shipping component and its listeners in place.
  // Only the delivered-total panel joins it within the same visible frame.
  if (deliveryBlock) {
    deliveryBlock.classList.add('dj-vp-shipping-total');
    deliveryBlock.append(box);
  } else purchase.before(box);
  const write = (node, value) => { if (node.textContent !== value) node.textContent = value; };
  const cents = value => value != null && /^\d+$/.test(value) && Number.isSafeInteger(Number(value)) ? Number(value) : null;
  const update = () => {
    const manual = hasConfiguration && root.dataset.djMountMode === 'manual';
    const committed = manual && root.hasAttribute('data-dj-manual-committed');
    const input = form.querySelector('[name="quantity"]');
    const quantity = committed ? 4 : Number(input?.value);
    const source = root.querySelector('directjantes-product-total');
    const unit = cents(source?.dataset.unitCents);
    const sameVariant = source?.dataset.variantId === form.querySelector('[name="id"]')?.value;
    const subtotal = committed ? cents(root.dataset.djManualSubtotalCents) : sameVariant && unit !== null ? unit * quantity : null;
    const shipping = root.querySelector('directjantes-product-shipping');
    const delivery = cents(shipping?.dataset.unitCents);
    const valid = (!manual || committed) && Number.isSafeInteger(quantity) && quantity > 0
      && (committed || (!!input && input.validity.valid)) && subtotal !== null && Number.isSafeInteger(subtotal);
    const total = valid && delivery !== null && delivery > 0 ? subtotal + delivery * quantity : null;
    const ready = total !== null && Number.isSafeInteger(total);
    write(label, valid ? `Total pour ${quantity} ${quantity === 1 ? 'jante' : 'jantes'}` : 'Total de votre sélection');
    write(note, ready ? 'Livraison incluse'
      : manual && !committed ? 'Choisissez vos dimensions pour afficher le total livré.'
      : valid ? 'Livraison à confirmer au panier.' : 'Sélectionnez une quantité valide pour afficher le total.');
    amount.hidden = !ready;
    if (ready) write(amount, `${(total / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € HT`);
    const shippingSelection = shipping?.querySelector('[data-dj-shipping-selection]');
    if (shippingSelection) shippingSelection.hidden = !ready;
  };
  for (const event of ['input', 'change', 'quantity-selector:update']) form.addEventListener(event, update);
  // Selection amounts come from the configurator's validated cents, never from
  // display text or an average of two different axle prices.
  const observer = new MutationObserver(() => {
    if (!root.isConnected) { observer.disconnect(); return; }
    update();
  });
  observer.observe(root, { attributes: true, attributeFilter: ['data-dj-mount-mode', 'data-dj-manual-committed', 'data-dj-manual-subtotal-cents'] });
  observer.observe(form, { subtree: true, childList: true, attributes: true, attributeFilter: ['value', 'min', 'max', 'step', 'disabled'] });
  const pricing = root.querySelector('product-price');
  if (pricing) observer.observe(pricing, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-unit-cents', 'data-variant-id'] });
  const shipping = root.querySelector('directjantes-product-shipping');
  if (shipping) observer.observe(shipping, { attributes: true, attributeFilter: ['data-unit-cents'] });
  update();
}

function init(root) {
  if (!root || root.dataset.djVehicleProductReady) return;
  const url = new URL(location.href);
  if (url.searchParams.get('dj_vehicle') !== '1') return;
  const options = root.querySelector('.dj-product-options');
  const lots = options?.querySelector('directjantes-product-quantity');
  const form = root.querySelector('.product-details product-form-component form');
  if (!options || !lots || !form) return;
  root.dataset.djVehicleProductReady = 'true';
  root.classList.add('dj-vehicle-product');
  const car = context(url);
  const suffix = form.id || 'product';
  const configuration = configurationPanel(root, suffix);
  let manualConfiguration;

  const geometry = root.querySelector('.dj-product-heading__geometry');
  if (geometry) {
    const diameter = geometry.dataset.djDiameter;
    const pcd = geometry.dataset.djPcd;
    // Real geometry comes from Liquid metafields, never from user-entered URL values.
    if (diameter && pcd) {
      geometry.replaceChildren(
        element('strong', 'dj-vp-diameter', `${diameter} pouces`),
        document.createTextNode(' '),
        element('span', 'dj-vp-pcd', `Entraxe ${pcd.replaceAll('x', '×')}`)
      );
    }
  }

  const choice = element('div', 'dj-vp-mount');
  choice.append(element('p', 'dj-product-label', 'Montage'));
  const group = element('div', 'dj-vp-choices');
  group.setAttribute('role', 'group');
  group.setAttribute('aria-label', 'Choix du montage');
  const buttons = new Map();
  for (const [value, label] of Object.entries(LABELS)) {
    const card = element('div', 'dj-vp-choice');
    const button = element('button', 'dj-vp-choice-button', label);
    button.type = 'button';
    button.dataset.djMount = value;
    button.setAttribute('aria-pressed', 'false');
    if (configuration && value === 'manual') button.setAttribute('aria-controls', configuration.panel.id);
    card.append(button);
    group.append(card);
    buttons.set(value, { card, button });
  }
  choice.append(group);
  options.append(choice);

  if (configuration) lots.remove();
  else {
    const fallback = element('div', 'dj-vp-manual-fallback');
    fallback.append(lots);
    options.after(fallback);
  }

  const assurance = root.querySelector('.dj-product-assistance');
  let vehicleAdvice, vehicleCheck;
  if (assurance && car) {
    assurance.classList.add('dj-vp-vehicle');
    const content = element('div', 'dj-vp-vehicle-content');
    const heading = element('div', 'dj-vp-vehicle-heading');
    const change = element('a', 'dj-vp-change-vehicle', 'Modifier mon véhicule');
    const finder = new URL(window.Shopify?.routes?.root || '/', location.origin);
    finder.hash = 'recherche-vehicule';
    if (url.searchParams.has('preview_theme_id')) finder.searchParams.set('preview_theme_id', url.searchParams.get('preview_theme_id'));
    change.href = finder.pathname + finder.search + finder.hash;
    change.dataset.djSearch = 'vehicle';
    heading.append(element('p', 'dj-vp-vehicle-name', `Votre ${car.name}`));
    content.append(heading);
    const info = [car.year, car.motor, car.fuel, car.power ? `${car.power.replace('.', ',')} CV` : ''].filter(Boolean).join(' · ');
    if (info) content.append(element('p', 'dj-vp-vehicle-details', info));
    const confirmation = element('p', 'dj-vp-vehicle-confirmation');
    const icon = assurance.querySelector('svg');
    if (icon) confirmation.append(icon);
    const message = element('span', 'dj-vp-vehicle-message');
    vehicleAdvice = element('strong', 'dj-vp-vehicle-advice');
    vehicleCheck = element('span', 'dj-vp-vehicle-check');
    message.append(vehicleAdvice, document.createTextNode(' '), vehicleCheck);
    confirmation.append(message);
    content.append(confirmation,
      element('p', 'dj-vp-vehicle-rings', 'Bagues de centrage fournies si nécessaire.'),
      element('p', 'dj-vp-vehicle-hardware', 'Visserie vérifiée selon le montage.'));
    const visual = element('div', 'dj-vp-vehicle-visual');
    visual.append(change);
    assurance.replaceChildren(content, visual);
    if (car.image) {
      const photo = element('img', 'dj-vp-vehicle-photo');
      photo.src = car.image;
      photo.alt = `${car.name} — photo indicative de la génération`;
      photo.width = 600; photo.height = 400;
      photo.decoding = 'async'; photo.referrerPolicy = 'no-referrer';
      photo.addEventListener('error', () => photo.remove(), { once: true });
      visual.prepend(photo);
    }
  }
  orderPurchaseBlocks(root, form);
  deliveredTotal(root, form, !!configuration);

  let mode = url.searchParams.get('dj_montage') === 'manual' ? 'manual' : 'expert';
  const inputs = new Map();
  function property(name, value) {
    if (!value) return;
    let input = inputs.get(name);
    if (!input) {
      input = element('input'); input.type = 'hidden'; input.name = `properties[${name}]`;
      form.append(input); inputs.set(name, input);
    }
    input.value = value;
  }
  function syncProperties() {
    property('Montage', mode === 'expert' ? EXPERT : MANUAL);
    property('_dj_montage', mode);
    if (car) {
      const description = [car.name, car.year, car.motor, car.fuel, car.power ? `${car.power} CV` : ''].filter(Boolean).join(' · ');
      property('Modèle du véhicule', description);
      property('_dj_selected_vehicle', description);
    }
  }
  function decorateLinks() {
    for (const link of root.querySelectorAll('.dj-product-dimensions a[href], .dj-product-finishes a[href]')) {
      const target = new URL(link.href, location.href);
      if (target.origin !== location.origin || !target.pathname.includes('/products/')) continue;
      target.searchParams.set('dj_vehicle', '1');
      target.searchParams.set('dj_montage', mode);
      if (car) target.searchParams.set('dj_car', JSON.stringify({ v: 1, ...car }));
      for (const key of ['dj_diameter', 'dj_pcd', 'preview_theme_id']) {
        if (url.searchParams.has(key)) target.searchParams.set(key, url.searchParams.get(key));
      }
      link.setAttribute('href', target.pathname + target.search + target.hash);
    }
  }
  function select(next, userInitiated = false) {
    mode = next;
    root.dataset.djMountMode = mode;
    if (vehicleAdvice && vehicleCheck) {
      vehicleAdvice.textContent = mode === 'expert'
        ? 'Nous sélectionnons les dimensions adaptées à votre véhicule.'
        : 'Choisissez vous-même vos dimensions.';
      vehicleCheck.textContent = mode === 'expert'
        ? 'Compatibilité vérifiée avant expédition.'
        : 'Notre équipe vérifie la compatibilité avant expédition.';
    }
    for (const [value, { card, button }] of buttons) {
      card.classList.toggle('is-selected', value === mode);
      button.setAttribute('aria-pressed', String(value === mode));
    }
    if (configuration) configuration.details.open = mode === 'manual';
    manualConfiguration?.setActive(mode === 'manual');
    const fallback = root.querySelector('.dj-vp-manual-fallback');
    if (fallback) fallback.hidden = mode !== 'manual';
    if (mode === 'expert') {
      const quantity = form.querySelector('[name="quantity"]');
      const min = Number(quantity?.min) || 1, step = Number(quantity?.step) || 1;
      const max = Math.min(Number(quantity?.max) || Infinity, Number(lots.dataset.inventoryLimit) || Infinity);
      if (quantity && !quantity.disabled && min <= 4 && max >= 4 && (4 - min) % step === 0) {
        quantity.value = '4';
        quantity.dispatchEvent(new Event('input', { bubbles: true }));
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
        quantity.closest('quantity-selector-component')?.updateButtonStates?.();
      }
    }
    const current = new URL(location.href);
    current.searchParams.set('dj_montage', mode);
    if (mode === 'expert') { current.searchParams.delete('dj_monte'); current.searchParams.delete('dj_pair'); }
    try { history.replaceState(history.state, '', current); } catch { /* Options still work. */ }
    syncProperties(); decorateLinks();
    if (userInitiated && mode === 'manual' && configuration) scrollToConfiguration(configuration.summary);
  }
  for (const [value, { button }] of buttons) button.addEventListener('click', () => select(value, true));
  form.addEventListener('formdata', event => {
    for (const input of inputs.values()) event.formData.set(input.name, input.value);
  });
  form.addEventListener('change', decorateLinks);
  if (configuration) {
    manualConfiguration = createConfiguration({
      root, form, ...configuration,
      getProperties: () => Object.fromEntries([...inputs].map(([name, input]) => [name, input.value])),
      activate: () => select('manual'),
    });
  }
  const pair = root.querySelector('[data-dj-configuration-mode="pair"]');
  refinePresentation(root, form, !!pair && !pair.hidden);
  select(mode);
}

function start(scope = document) {
  for (const root of scope.querySelectorAll('.product-information')) init(root);
}
start();
document.addEventListener('shopify:section:load', event => start(event.target));
